import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import { createElement, FC } from 'react';
import { Button } from 'antd';
import { colorConstants } from 'config/colorConstants';

const lensTabInactiveBg = '#E4EBF780';
export const LENS_SETTINGS_DROPDOWN_CLASS = 'price-simulator-v3-lens-settings-dropdown';

const lensSettingsDropdownGlobalStyles = css`
    .${LENS_SETTINGS_DROPDOWN_CLASS} .ant-dropdown-menu {
        padding: 0;
        background: transparent;
        box-shadow: none;
        overflow: visible;
    }

    .${LENS_SETTINGS_DROPDOWN_CLASS}.ant-dropdown {
        overflow: visible;
    }

    .${LENS_SETTINGS_DROPDOWN_CLASS} .ant-dropdown-menu-root {
        overflow: visible;
    }
`;

export const StyledLensSettingsDropdownGlobalStyles: FC = () =>
    createElement(Global, { styles: lensSettingsDropdownGlobalStyles });

const lensTabInactiveText = colorConstants.inactiveTabText;
const lensTabActiveBg = colorConstants.greyAlt11;
const lensTabActiveBorder = colorConstants.purpleFont;
const lensTabBorder = '#E4E4E7';
const lensTabHeight = '36px';
const lensTabGap = '6px';
const lensTabMinWidth = '150px';

const lensActiveLightBg = '#f5f3ff';
const lensSelectedBorder = '#d4c9f7';
const lensDividerColor = 'rgba(0, 0, 0, 0.06)';

const lensSearchInputSuffixStyles = `
    .ant-input-affix-wrapper {
        padding-inline-end: 0;
        padding-block: 0;
        align-items: stretch;
    }

    .ant-input {
        padding-block: 4px;
    }

    .ant-input-suffix {
        display: flex;
        align-items: center;
        align-self: stretch;
        margin-inline-start: 0;
        padding-inline: 8px;
        border-left: 1px solid ${colorConstants.borderGreyAlt};
    }
`;

export const StyledLensTabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${lensTabGap};
    margin-top: 12px;
    padding-bottom: 2px;
    min-width: 0;
`;

export const StyledLensTabsTrack = styled.div`
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.15);
    }
`;

export const StyledLensTabsInner = styled.div`
    display: flex;
    align-items: center;
    gap: ${lensTabGap};
    min-width: 100%;
`;

export const StyledSettingsButton = styled(Button)`
    &.ant-btn {
        width: ${lensTabHeight};
        height: ${lensTabHeight};
        min-width: ${lensTabHeight};
        padding: 0;
        border: 1px solid ${lensTabBorder};
        border-radius: 4px;
        box-shadow: none;
        color: rgba(0, 0, 0, 0.55);
        background: ${colorConstants.white};
        display: inline-flex;
        align-items: center;
        justify-content: center;

        .anticon {
            font-size: 16px;
        }

        &:hover,
        &:focus {
            color: ${lensTabActiveBorder} !important;
            background: ${lensTabActiveBg} !important;
            border-color: ${lensTabActiveBorder} !important;
        }
    }
`;

export const StyledLensTab = styled.button<{ isActive: boolean }>`
    position: relative;
    flex: 1 0 ${lensTabMinWidth};
    min-width: ${lensTabMinWidth};
    height: ${lensTabHeight};
    padding: 0 14px;
    border-radius: 4px;
    background: ${({ isActive }) => (isActive ? lensTabActiveBg : lensTabInactiveBg)};
    color: ${({ isActive }) => (isActive ? lensTabActiveBorder : lensTabInactiveText)};
    font-size: 13px;
    line-height: 20px;
    font-weight: ${({ isActive }) => (isActive ? 500 : 400)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    cursor: pointer;
    transition:
        background-color 0.2s ease,
        color 0.2s ease,
        border-color 0.2s ease;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: ${({ isActive }) => (isActive ? '1px' : '0')};
        background: ${lensTabActiveBorder};
        border-radius: 0 0 3px 3px;
    }

    &:hover {
        background: ${({ isActive }) => (isActive ? lensTabActiveBg : lensTabInactiveBg)};
        color: ${({ isActive }) => (isActive ? lensTabActiveBorder : 'rgba(0, 0, 0, 0.75)')};
        border-color: ${({ isActive }) => (isActive ? lensTabActiveBorder : '#D4D4D8')};
    }
`;

export const StyledLensSettingsPanel = styled.div`
    width: 240px;
    flex-shrink: 0;
    border-radius: 8px;
    height: 326px;
    padding: 12px 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: ${colorConstants.white};
    box-shadow:
        0 3px 6px -4px rgba(0, 0, 0, 0.12),
        0 6px 16px 0 rgba(0, 0, 0, 0.08),
        0 9px 28px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
`;

export const StyledLensSettingsDropdownWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: flex-start;
`;

export const LENS_CONFIGURATION_DROPDOWN_WIDTH = 560;
export const LENS_CONFIGURATION_DROPDOWN_HEIGHT = 412;

export const StyledLensConfigurationDropdown = styled.div`
    position: absolute;
    top: 0;
    left: 100%;
    width: ${LENS_CONFIGURATION_DROPDOWN_WIDTH}px;
    height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
    min-height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
    border-radius: 8px;
    background: ${colorConstants.white};
    box-shadow:
        0 3px 6px -4px rgba(0, 0, 0, 0.12),
        0 6px 16px 0 rgba(0, 0, 0, 0.08),
        0 9px 28px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    z-index: 2;
`;

export const StyledLensConfigurationLoading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
`;

export const StyledLensSettingsSearch = styled.div`
 
    ${lensSearchInputSuffixStyles}
`;

export const StyledLensSettingsList = styled.div`
    flex: 1;
    height: 230px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 4px;
`;

export const StyledLensSettingsItem = styled.div<{
    isDragging?: boolean;
    isDropTarget?: boolean;
    isExpanded?: boolean;
    isSelected?: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 4px ;
    border-radius: 6px;
    border: 1px solid
        ${({ isDropTarget, isSelected }) => {
            if (isDropTarget) {
                return colorConstants.grey;
            }
            return isSelected ? colorConstants.grey : colorConstants.white;
        }};
    background: ${({ isSelected }) =>
        isSelected ? lensActiveLightBg : colorConstants.white};
    opacity: ${({ isDragging }) => (isDragging ? 0.7 : 1)};
    cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'pointer')};

    .custom-lens-user-icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        font-size: 14px;
    }

    .lens-label {
        flex: 1;
        font-size: 13px;
        line-height: 22px;
        color: ${({ isSelected }) => (isSelected ? colorConstants.purpleFont : 'rgba(0, 0, 0, 0.88)')};
        font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const StyledLensExpandButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        color: ${colorConstants.purpleFont};
        background: ${lensActiveLightBg};
    }
`;

export const StyledCustomLensRow = styled.button<{ isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    border-top: 1px solid ${lensDividerColor};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    text-align: left;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.45 : 1)};

    &:hover {
        background: ${({ isDisabled }) => (isDisabled ? 'transparent' : lensActiveLightBg)};
    }

    .plus-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 1px solid
            ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        color: ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        font-size: 11px;
        line-height: 1;
    }

    .custom-lens-label {
        font-size: 13px;
        line-height: 22px;
        color: ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        font-weight: 500;
    }
`;

export const StyledLensSettingsFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 4px;
    padding-top: 12px;
    border-top: 1px solid ${lensDividerColor};
`;

const lensFooterButtonStyles = `
    &.ant-btn {
        width: 100px;
        height: 32px;
        min-width: 100px;
        padding: 0;
        border-radius: 8px;
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: none;
    }
`;

export const StyledLensFooterResetButton = styled(Button)`
    ${lensFooterButtonStyles}

    &.ant-btn:not([disabled]) {
        color: ${colorConstants.purpleFont};
        border: 1px solid ${colorConstants.purpleFont};
        background: ${colorConstants.white};

        &:hover,
        &:focus {
            color: ${colorConstants.purpleFont} !important;
            border-color: ${colorConstants.purpleFont} !important;
            background: ${lensActiveLightBg} !important;
        }
    }

    &.ant-btn[disabled],
    &.ant-btn[disabled]:hover,
    &.ant-btn[disabled]:focus,
    &.ant-btn[disabled]:active {
        color: rgba(0, 0, 0, 0.25) !important;
        border-color: #d9d9d9 !important;
        background: #f5f5f5 !important;
        opacity: 1;
        cursor: not-allowed;
    }
`;

export const StyledLensFooterApplyButton = styled(Button)`
    ${lensFooterButtonStyles}

    &.ant-btn.ant-btn-primary:not([disabled]) {
        color: ${colorConstants.white};
        border: 1px solid ${colorConstants.purpleFont};
        background: ${colorConstants.purpleFont};

        &:hover,
        &:focus {
            color: ${colorConstants.white} !important;
            border-color: ${colorConstants.purpleFont} !important;
            background: ${colorConstants.purpleFont} !important;
            opacity: 0.92;
        }
    }

    /* Match Ant Design primary disabled (same as Create Custom Lens save button). */
    &.ant-btn.ant-btn-primary[disabled],
    &.ant-btn.ant-btn-primary[disabled]:hover,
    &.ant-btn.ant-btn-primary[disabled]:focus,
    &.ant-btn.ant-btn-primary[disabled]:active {
        color: rgba(0, 0, 0, 0.25) !important;
        border-color: #d9d9d9 !important;
        background: #f5f5f5 !important;
        opacity: 1;
        cursor: not-allowed;
    }
`;

export const StyledLensContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 320px;
    margin-top: 20px;
    background: ${colorConstants.white};
    border-radius: 4px;
`;

export const StyledLensContextBannerFooter = styled.div`
    margin-top: auto;
`;

export const StyledLensConfigPanelBody = styled.div`
    display: flex;
    flex-direction: column;
    height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
`;

export const StyledLensConfigPanelHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 8px;
    padding-bottom: 10px;
`;

export const StyledLensConfigEditButton = styled.button`
    flex-shrink: 0;
    height: 32px;
    padding: 0 15px;
    border-radius: 8px;
    border: 1px solid ${colorConstants.greyAlt};
    color: #52525c;
    background: ${colorConstants.white};
    font-size: 14px;
    line-height: 22px;
    font-weight: 400;
    cursor: pointer;
    box-shadow: none;

    &:hover,
    &:focus {
        color: ${colorConstants.purpleFont};
        border-color: ${colorConstants.purpleFont};
        background: ${lensActiveLightBg};
        outline: none;
    }
`;

export const StyledLensConfigSearch = styled.div`
    width: 170px;

    ${lensSearchInputSuffixStyles}
`;

export const StyledLensConfigSystemRow = styled.div`
    padding: 14px 8px 8px;

    .ant-checkbox-wrapper {
        font-size: 13px;
        line-height: 22px;
        color: rgba(0, 0, 0, 0.88);
    }
`;

export const StyledLensConfigColumns = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    flex: 1;
    min-height: 0;
`;

export const StyledLensConfigFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 12px 8px 8px;
    flex-shrink: 0;
`;

export const StyledLensConfigColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;

    &:first-of-type {
        border-right: 1px solid ${lensDividerColor};
    }
`;

export const StyledLensConfigColumnHeader = styled.div<{ uiRole?: 'DIMENSION' | 'KPI' }>`
    padding-top: 10px;
    padding-bottom: 6px;
    padding-right: ${({ uiRole }) => (uiRole === 'DIMENSION' ? '8px' : '18px')};
    padding-left: ${({ uiRole }) => (uiRole === 'KPI' ? '18px' : '8px')};
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.88);
`;

export const StyledLensConfigColumnList = styled.div<{ uiRole?: 'DIMENSION' | 'KPI' }>`
    flex: 1;
    overflow-y: auto;
    padding-top: 0;
    padding-bottom: 0;
    padding-right: ${({ uiRole }) => (uiRole === 'DIMENSION' ? '16px' : '4px')};
    padding-left: ${({ uiRole }) => (uiRole === 'KPI' ? '16px' : '4px')};
    display: flex;
    flex-direction: column;
    gap: 2px;

    .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-inner {
        background-color: ${colorConstants.purpleFont} !important;
        border-color: ${colorConstants.purpleFont} !important;
    }

    /* Partial "(All)" selection: plain checkbox, no purple border/fill */
    .ant-checkbox-indeterminate .ant-checkbox-inner {
        background-color: ${colorConstants.white} !important;
        border-color: #d9d9d9 !important;
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        background-color:rgb(255, 255, 255) !important;
    }

    .ant-checkbox-wrapper:hover .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-indeterminate):hover .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: ${colorConstants.purpleFont};
    }

    .ant-checkbox-indeterminate:hover .ant-checkbox-inner,
    .ant-checkbox-indeterminate .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: #d9d9d9 !important;
    }

    .lens-config-item-checkbox.ant-checkbox-wrapper {
        display: flex;
        width: 100%;
        margin-inline-start: 0;
        align-items: center;
        line-height: 22px;
    }

    .lens-config-item-checkbox .ant-checkbox {
        top: 0;
        align-self: center;
    }

    .lens-config-item-checkbox .ant-checkbox + span {
        flex: 1;
        min-width: 0;
        display: inline-flex;
        align-items: center;
        padding-inline-start: 8px;
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const StyledLensConfigItem = styled.div<{
    isDisabled?: boolean;
    isSelected?: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 4px;
    border-radius: 6px;
    border: 1px solid
        ${({ isSelected }) => (isSelected ? colorConstants.grey : colorConstants.white)};
    background: ${({ isSelected }) => (isSelected ? lensActiveLightBg : colorConstants.white)};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ isDisabled }) => (isDisabled ? 0.55 : 1)};

    .item-label {
        flex: 1;
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;
